import { useState } from 'react';

/**
 * Pins a CSP nonce to the first non-null value this component instance ever
 * sees, ignoring every later value the caller passes in. The browser only ever
 * enforces the `Content-Security-Policy(-Report-Only)` header from the current
 * document's ORIGINAL top-level navigation response — it never re-adopts a
 * nonce from a later fetch (an RSC-payload request, or on the Pages Router a
 * client-side `getInitialProps` re-run) triggered by a client-side transition.
 * Only useful in a component that itself persists across those transitions
 * (e.g. `_app.tsx`'s `MyApp`, which never remounts between pages) — a
 * component that remounts per-navigation would just reset the pin every time.
 *
 * Implemented as state rather than a ref: conditionally calling the setter
 * during render, guarded so the condition can only ever fire once (it stops
 * being true the render after it sets `pinnedNonce`), is React's own
 * documented pattern for adjusting state from a changing prop — unlike reading
 * a ref during render, which the lint rules here forbid outright.
 */
export const usePinnedCspNonce = (nonce: string | null | undefined): string | null => {
	const [pinnedNonce, setPinnedNonce] = useState<string | null>(nonce ?? null);
	if (pinnedNonce === null && nonce) {
		setPinnedNonce(nonce);
	}
	return pinnedNonce;
};
